import axios from "axios"
import { useCallback, useEffect, useRef, useState } from "react"
import { toast } from "react-toastify"
import { Link, Navigate, useLocation } from "react-router-dom"
import { StartFlow } from "./StartFlow"
import { EmailOtp } from "./EmailOtp"
import { Passkey } from "./Passkey"
import { startAuthentication, WebAuthnAbortService } from "@simplewebauthn/browser"
import { Button, Spinner } from "react-bootstrap"

type Challenge = "email-otp" | "webauthn"

export interface SignInProps {
  clientId: string
  redirectUri: string
  codeChallenge: string
}

export const SignIn = () => {
  const [redirecting, setRedirecting] = useState(false)
  const [email, setEmail] = useState("")
  const [lastUsedEmail, setLastUsedEmail] = useState<string | null>(null)
  const [session, setSession] = useState<string | null>(null)
  const [webauthnParams, setWebauthnParams] = useState<any | null>(null)
  const [challenge, setChallenge] = useState<Challenge | null>(null)
  const [autoPasskeyAuthInProgress, setAutoPasskeyAuthInProgress] = useState(false)
  const autoPasskeyEnabled = false
  const triggered = useRef(false)

  const location = useLocation()

  const clientId: string = location.state?.clientId
  const redirectUri: string = location.state?.redirectUri
  const codeChallenge: string = location.state?.codeChallenge
  const state: string = location.state?.state

  const startSignInOnBackend = useCallback(async (email: String, preferredChallenge?: "email-otp" | "webauthn") => {
    const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/sign-in/start`, {
      email,
      clientId,
      preferredChallenge: preferredChallenge ?? "webauthn"
    })

    return response.data
  }, [clientId])

  const startSignIn = async (email: string, preferredChallenge?: "email-otp" | "webauthn") => {
    try {
      const data = await startSignInOnBackend(email, preferredChallenge)

      setEmail(email)
      setSession(data.session)

      if (data.challenge['emailOtp']) {
        setChallenge("email-otp")
      } else if (data.challenge['webAuthn']) {
        setChallenge("webauthn")
        setWebauthnParams(data.challenge.webAuthn.parameters)
      }
    } catch (e) {
      console.error(e)
      toast.error("Unexpected error")
    }
  }

  const sendEmailOtp = async (code: string) => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/sign-in/challenges/email-otp/response`, {
        email,
        clientId,
        redirectUri,
        codeChallenge,
        codeChallengeMethod: "S256",
        session,
        code
      })

      setRedirecting(true)
      redirectSuccess(response.data.authorizationCode)
    } catch (error) {
      console.log(error)
      if ((error as any).response?.status === 401) {
        toast.error("Invalid code")
      } else {
        console.error(error)
        toast.error("Unexpected error")
      }
    }
  }

  const redirectSuccess = useCallback((code: string) => {
    if (process.env.REACT_APP_DISABLE_REDIRECTION === "true") {
      toast.success("Success!")
      reset()
    } else {
      setRedirecting(true)
      window.location.href = `${redirectUri}?state=${state}&code=${code}`
    }
  }, [redirectUri, state])

  /* const redirectError = () => {
    window.location.href = `${redirectUri}?state=${state}&error=access_denied`
  } */ // TODO

  const sendWebAuthnResponse = useCallback(async (email: string, session: string, authenticationResponse: any) => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/sign-in/challenges/webauthn/response`, {
        email,
        clientId,
        redirectUri,
        codeChallenge,
        codeChallengeMethod: "S256",
        session,
        credential: authenticationResponse
      })

      localStorage.setItem("lastUsedEmail", email)

      redirectSuccess(response.data.authorizationCode)
    } catch (error) {
      console.log(error)
      if ((error as any).response?.status === 401) {
        toast.error("Invalid code")
      } else {
        console.error(error)
        toast.error("Unexpected error")
      }
    }
  }, [redirectSuccess, redirectUri, clientId, codeChallenge])

  const reset = () => {
    setEmail("")
    setSession(null)
    setWebauthnParams(null)
    setChallenge(null)
  }

  const performAutoPasskeyAuth = useCallback(async (email: string) => {
    if (!triggered.current) {
      try {
        setAutoPasskeyAuthInProgress(true)
        const response = await startSignInOnBackend(email, "webauthn")

        triggered.current = true
        const webAuthnResponse = await startAuthentication({ optionsJSON: response.challenge.webAuthn.parameters, useBrowserAutofill: true })
        await sendWebAuthnResponse(email, response.session, webAuthnResponse)
      }
      catch (error) {
        console.warn(error)
      } finally {
        setAutoPasskeyAuthInProgress(false)
        triggered.current = false
      }
    }
  }, [setAutoPasskeyAuthInProgress, startSignInOnBackend, sendWebAuthnResponse])

  useEffect(() => {
    const lastUsedEmail = localStorage.getItem("lastUsedEmail")
    if (lastUsedEmail) {
      setLastUsedEmail(lastUsedEmail)
    }
  }, [])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      console.log("keydown", event.key, lastUsedEmail)
      if (event.key === 'Escape' && autoPasskeyAuthInProgress) {
        console.info("Cancelling passkey auth...")
        WebAuthnAbortService.cancelCeremony()
      }

      if (event.key === 'Enter' && lastUsedEmail && event.metaKey) {
        performAutoPasskeyAuth(lastUsedEmail)
      }
    }

    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [autoPasskeyAuthInProgress, lastUsedEmail, performAutoPasskeyAuth])

  useEffect(() => {
    if (autoPasskeyEnabled && lastUsedEmail && !autoPasskeyAuthInProgress && !redirecting) {
      performAutoPasskeyAuth(lastUsedEmail)
    }
  }, [autoPasskeyEnabled, lastUsedEmail, performAutoPasskeyAuth, autoPasskeyAuthInProgress, redirecting])

  if (clientId && redirectUri && codeChallenge) {

    return (
      <>
        <div>
          {redirecting ? <div className="d-flex flex-column align-items-center gap-2">
            <Spinner animation="border" />
            <h5>Redirecting...</h5>
          </div> :
            <>
              <h5>Sign In</h5>
              {(challenge === null) && <>
                <div className="d-flex flex-column mt-2 text-center gap-2">
                  <StartFlow onSubmit={startSignIn} />
                  {<Button variant="dark" style={{ background: "#333" }} disabled={autoPasskeyAuthInProgress || !lastUsedEmail} onClick={() => performAutoPasskeyAuth(lastUsedEmail!)}>Sign in with passkey</Button>}
                  <div className="fs-6 mt-2">
                    <Link to="/passkeys" state={{ clientId }} className="text-decoration-none">Manage passkeys</Link>
                  </div>
                </div>
              </>}
              {(challenge === "email-otp") && <EmailOtp onSubmit={sendEmailOtp} onCancel={reset} />}
              {(challenge === "webauthn" && session) && <Passkey webAuthnParams={webauthnParams} onSuccess={(response) => sendWebAuthnResponse(email, session, response)} onCancel={reset} onAlternativeSelection={() => startSignIn(email, "email-otp")} />}
            </>
          }
        </div>
      </>
    )
  } else {
    return <Navigate to="/error" />
  }
}
